import { Id } from '@silevis/reactgrid';
import moment from 'moment';

import { CHART_COLORS } from 'projectConstants';

export interface FilterItem {
  name: string;
  status: boolean;
  search: boolean;
}

export interface FilteredItem {
  dimId: string | number;
  filterItems: FilterItem[];
}

export interface TableObject {
  [key: string]: {
    value: string;
    id?: string | number;
  };
}

export interface ProcessedFilterItem {
  dimensionId: string;
  filterItems: string[];
}

export interface ColumnHeader {
  id: string | number;
  name: string;
  is_dimension: boolean;
}

export interface TableData {
  column_headers: ColumnHeader[];
  excel_data: Array<Record<string, any>>;
  dimensions: Array<{ id: string | number; name: string }>;
  type: string;
  indicator_id?: string;
}

export interface Column {
  columnId: string;
  name: string;
  width: number;
  resizable: boolean;
  reorderable: boolean;
  timeDimId?: string | number;
}

export interface Cell {
  type: 'header' | 'text';
  colType?: 'header';
  nonEditable?: boolean;
  property?: string;
  id?: string | number;
  text: string;
  className?: string;
  data?: any;
  rowId?: string | number;
  currencyType?: string;
  uniqueId?: number;
  format?: string;
  color?: string;
  rows?: number;
  itemmId?: string;
  index?: number;
  columnId?: string;
}

export interface Row {
  rowId: string;
  height: number;
  reorderable?: boolean;
  cells: Cell[];
}

export interface TableItem {
  id: string;
  [key: string]: any;
}

export interface CellChange {
  rowId: string;
  columnId: string;
  newCell: {
    text: string;
    [key: string]: any;
  };
}

export interface UpdatePayload {
  indicator_id: string;
  request_body: {
    bulk_input: boolean;
    format_excel_data: string;
    dimensions: Array<{ id: string | number; name: string }>;
    data_values: Array<Record<string, any>>;
    type: string;
    scenario_id: string | number;
  };
}

export interface Dimension {
  id: string | number;
  name: string;
}

export const monthNames: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const getColumnId = (
  header: ColumnHeader,
  currentType: string,
  timeDimensionId?: string | number,
): string => {
  if (header.is_dimension) return `${header.id}`;
  if (currentType === 'constant_by_year' || currentType === 'raw') {
    return `${timeDimensionId} ${header.name}`;
  }
  return `${header.name}`;
};

export const isHeaderNonEditable = (data: ColumnHeader, date?: string): boolean => {
  if (data.is_dimension) return true;
  if (!date) return false;

  const isMonthlyNonEditable = (dataHeading: string): boolean => {
    const headingParts = dataHeading.split('-');
    const scenarioParts = date.split('-');
    if (headingParts.length < 2 || scenarioParts.length < 2) return false;

    const headingYear = Number(headingParts[1]);
    const scenarioYear = Number(scenarioParts[1]);
    if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

    if (headingYear < scenarioYear) return true;
    return (
      headingYear === scenarioYear &&
      monthNames.indexOf(headingParts[0]) <= monthNames.indexOf(scenarioParts[0])
    );
  };

  if (data.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(data.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  return isMonthlyNonEditable(data.name);
};

export const createHeaderRow = (tableProperty: ColumnHeader[]): Row => ({
  rowId: 'header',
  height: 34,
  cells: tableProperty.map((data, index) => ({
    type: 'header',
    colType: 'header',
    nonEditable: true,
    property: data.is_dimension ? 'dimension' : '',
    id: data.id,
    text: data.name,
    className: `header-cell ${index === tableProperty.length - 1 ? 'header-last' : ''} ${
      index === 0 ? 'header-first' : ''
    }`,
    data,
  })),
});

export const isPercentageIndicatorFormat = (isPercentage: string): boolean =>
  isPercentage === 'percentage';

export const PERCENT_POINT_COLUMN_NAMES = new Set(['growth_rate', 'growth_value']);

/** Whether a data-input column uses decimal storage (×100 for display, ÷100 on save). */
export const usesDecimalPercentageStorage = (columnName: string, isPercentage: string): boolean =>
  isPercentageIndicatorFormat(isPercentage) && !PERCENT_POINT_COLUMN_NAMES.has(columnName);

/** Stored API value → grid display text (percent display points, e.g. 1.38 → 138). */
export const storedValueToPercentageDisplay = (
  stored: unknown,
  columnName: string,
  isPercentage: string,
): number | string => {
  if (stored === null || stored === undefined || stored === '') return stored as string;
  const numeric = Number(stored);
  if (!Number.isFinite(numeric)) return String(stored);
  if (!usesDecimalPercentageStorage(columnName, isPercentage)) return numeric;
  return numeric * 100;
};

export const createTableRows = (
  tableItems: TableItem[],
  tableHeaders: ColumnHeader[],
  isPercentage: string,
  modelCurrency?: string,
  date?: string,
): Row[] => {
  if (!tableItems?.length || !tableHeaders?.length) return [];

  const isCellNonEditable = (data: ColumnHeader): boolean => isHeaderNonEditable(data, date);
  const headerRow = createHeaderRow(tableHeaders);
  const dataRows = tableItems.map((item, index) => ({
    rowId: String(item.id),
    height: 34,
    reorderable: true,
    cells: tableHeaders.map((data, childIndex) => {
      const fData = data.is_dimension
        ? item[String(data.id)]
        : // eslint-disable-next-line @typescript-eslint/no-use-before-define
        usesDecimalPercentageStorage(data.name, isPercentage)
        ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
          storedValueToPercentageDisplay(item[data.name], data.name, isPercentage)
        : item[data.name];

      return {
        type: 'text' as const,
        currencyType: modelCurrency,
        property: data.is_dimension ? 'dimension' : '',
        uniqueId: fData?.uniqueid,
        format: isPercentage,
        color: CHART_COLORS[fData?.uniqueid],
        rows: tableItems.length,
        itemmId: data.is_dimension ? `${fData?.id}` : `${data.id}`,
        text: data.is_dimension ? `${fData?.value}` : `${fData}`,
        index,
        data,
        columnId: data.name,
        rowId: data.is_dimension ? String(data.id) : data.name,
        nonEditable: isCellNonEditable(data),
        className: [
          tableItems.length - 1 === index && !data.is_dimension ? 'border-right' : '',
          tableItems.length - 1 === index && childIndex === 0 ? 'border-bottom' : '',
          !data.is_dimension && isCellNonEditable(data) ? 'non-edit-bg' : '',
          data.is_dimension ? 'dimension-cell' : '',
        ]
          .filter(Boolean)
          .join(' '),
      };
    }),
  }));

  return [headerRow, ...dataRows];
};

export const calculateColumnWidth = (
  totalElements: number,
  totalWidth: number,
  currentType: string,
): number => {
  const isConstantByYear = currentType === 'constant_by_year';
  const isConstant = currentType === 'constant';
  const NAME_FIELD_WIDTH = 150;
  const EDIT_FIELD_WIDTH = 60;
  const MIN_COLUMN_WIDTH = isConstantByYear ? 120 : 100;
  const MAX_COLUMN_WIDTH = isConstantByYear ? 800 : 600;
  const PADDING = 20;
  // Integer widths avoid ReactGrid sticky-pane misalignment on scaled / low-DPI displays.
  const safeTotalWidth = Math.floor(Math.max(totalWidth, 0));

  if (isConstantByYear) {
    const availableWidth = safeTotalWidth - NAME_FIELD_WIDTH - EDIT_FIELD_WIDTH - PADDING;
    const baseWidth = Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(MIN_COLUMN_WIDTH, availableWidth / Math.min(totalElements, 12)),
    );
    return Math.floor(baseWidth);
  }

  if (isConstant) {
    const availableWidth = safeTotalWidth - EDIT_FIELD_WIDTH - PADDING;
    return Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(MIN_COLUMN_WIDTH, Math.floor(availableWidth / Math.max(totalElements, 1))),
    );
  }

  // Divide by the columns that actually render. This reserved both a
  // phantom extra column (`+ 1`) and an edit column that the revamp table no
  // longer has, so the grid always stopped short of its container and left a
  // band of empty space after the last period.
  return Math.min(
    MAX_COLUMN_WIDTH,
    Math.max(MIN_COLUMN_WIDTH, Math.floor((safeTotalWidth - PADDING) / Math.max(totalElements, 1))),
  );
};

export const extractYear = (dateString: string, modelDetails?: any) => {
  if (!modelDetails?.time_properties) return dateString;

  const modelStartYear = moment(modelDetails.time_properties.starting_period).year() % 1000;

  if (modelDetails.time_properties.time_granularity.toLowerCase() === 'm') {
    const parsedDate = moment(dateString, 'MMM-YY');
    const extractedYear = parsedDate.year() % 1000;

    const fiscalMonthIndex = monthNames.indexOf(modelDetails.time_properties.fy_start_month);
    const modelMonthIndex = moment(modelDetails.time_properties.starting_period).month();
    if (extractedYear === modelStartYear && modelMonthIndex >= fiscalMonthIndex) {
      return `FY${extractedYear + 1}`;
    }
    return `FY${extractedYear}`;
  }
  return `FY${Number(dateString) % 1000}`;
};

export const reorderHeadersByDimensionOrder = (
  headers: ColumnHeader[],
  dimensionOrder: string[],
): ColumnHeader[] => {
  if (dimensionOrder.length === 0) return headers;

  const dimensions = headers.filter((header) => header.is_dimension);
  const values = headers.filter((header) => !header.is_dimension);
  const dimensionById = new Map(dimensions.map((header) => [String(header.id), header]));
  const orderedDimensions = dimensionOrder
    .map((id) => dimensionById.get(String(id)))
    .filter(Boolean) as ColumnHeader[];
  const remainingDimensions = dimensions.filter(
    (header) => !dimensionOrder.includes(String(header.id)),
  );

  return [...orderedDimensions, ...remainingDimensions, ...values];
};

export const getReorderedDimensionIds = (
  currentIds: string[],
  targetColumnId: Id,
  columnIds: Id[],
): string[] => {
  const idsArray = [...currentIds];
  const targetIndex = idsArray.indexOf(String(targetColumnId));
  const columnIndex = idsArray.indexOf(String(columnIds[0]));

  if (targetIndex === -1 || columnIndex === -1) return idsArray;

  idsArray.splice(targetIndex, 1, ...columnIds.map(String));

  if (targetIndex > columnIndex) {
    idsArray.splice(
      idsArray.indexOf(String(columnIds[0])),
      columnIds.length,
      String(targetColumnId),
    );
  } else {
    idsArray.splice(
      idsArray.lastIndexOf(String(columnIds[0])),
      columnIds.length,
      String(targetColumnId),
    );
  }

  return idsArray;
};

export const buildColumnMetadata = (
  headers: ColumnHeader[],
  currentType: string,
  timeDimensionId?: string | number,
) => {
  const headerByColumnId = new Map<string, ColumnHeader>();
  const localKeyByColumnId = new Map<string, string>();

  headers.forEach((header) => {
    const columnId = getColumnId(header, currentType, timeDimensionId);
    headerByColumnId.set(columnId, header);
    localKeyByColumnId.set(columnId, header.name);
  });

  return { headerByColumnId, localKeyByColumnId };
};

/**
 * Growth inputs store rate as percent-points (10 = 10%). Value / time columns store
 * fractions (1.38 = 138%). Only the latter are scaled for grid edit/display.
 */

export const getLocalCellValue = (text: string, isPercentage: string, columnName?: string) => {
  if (text === '') return '0';
  if (columnName && usesDecimalPercentageStorage(columnName, isPercentage)) {
    return Number(text) / 100;
  }
  if (isPercentageIndicatorFormat(isPercentage) && !columnName) {
    return Number(text) / 100;
  }
  return text;
};

export const buildProcessedRowsData = (
  tableData: TableData,
  orderedColumnHeaders: ColumnHeader[],
): TableItem[] => {
  const uniqueIds: Record<string, number> = {};
  let counter = 0;

  const firstDimensionId = orderedColumnHeaders.find((header) => header.is_dimension)?.id;
  if (tableData.dimensions?.length === 0) {
    return tableData.excel_data.map((element: Record<string, any>, index: number) => ({
      ...element,
      id: String(index + 1),
    }));
  }

  return tableData.excel_data.map((element: Record<string, any>, index: number) => {
    const newElement: TableItem = { ...element, id: String(index + 1) };

    if (firstDimensionId && element[String(firstDimensionId)]?.id !== undefined) {
      const elementId = element[String(firstDimensionId)].id;
      if (uniqueIds[elementId] === undefined) {
        uniqueIds[elementId] = counter;
        newElement[String(firstDimensionId)] = {
          ...element[String(firstDimensionId)],
          uniqueid: counter,
        };
        counter += 1;
      } else {
        newElement[String(firstDimensionId)] = {
          ...element[String(firstDimensionId)],
          uniqueid: uniqueIds[elementId],
        };
      }
    }
    return newElement;
  });
};
